import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Flex,
  Link,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useField } from "formik";
import { FunctionComponent, useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { NoDataInfoAlert } from "src/components/Feedback/NoDataInfoAlert";
import { FormikCustomSelect } from "src/components/Inputs/CustomSelect";
import { Glossary } from "src/components/Text/Glossary";
import type { EligibleFormTemplatesForMeResponse } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import {
  canEditFormCheck,
  canLateEditCheck,
  isInProgressAndCanEditCheck,
} from "src/scenes/parent/forms/formEditability";
import { fullName } from "src/services/format";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { StudentPerson } from "../../types";

type Props = {
  eligibleTemplates: EligibleFormTemplatesForMeResponse;
  selectedStudent: StudentPerson | undefined;
  isLoading: boolean;
  isError: boolean;
};

export type FormAction = "start" | "resume" | "edit" | "late-edit" | "view";

export type FormTemplateOption = {
  label: string;
  description: string | null;
  key: string;
  value: string;
  disabled?: boolean;
  action: FormAction;
  existingFormId?: string;
};

export function getFormAction(
  existingForm: GQL.GetStudentsByGuardianId_person_forms | undefined
): FormAction {
  if (!existingForm) return "start";

  const { status, form_template } = existingForm;

  const sections = form_template.sections.map((s) => ({
    family_editable_statuses: s.family_editable_statuses ?? undefined,
  }));
  const enrollmentPeriod = form_template.enrollment_period;

  if (isInProgressAndCanEditCheck(sections, enrollmentPeriod, status)) {
    return "resume";
  }

  if (canEditFormCheck(sections, enrollmentPeriod, status)) return "edit";
  if (canLateEditCheck(sections, enrollmentPeriod, status)) return "late-edit";

  return "view";
}

function getBadgeProps(action: FormAction): { label: string } {
  switch (action) {
    case "start":
      return { label: "Start" };
    case "resume":
      return { label: "In progress - Resume" };
    case "edit":
    case "late-edit":
      return { label: "Submitted - Edit" };
    case "view":
      return { label: "Submitted - View" };
  }
}

const whiteBadgeProps = {
  backgroundColor: "white",
  color: "gray.800",
  borderWidth: "1px",
  borderColor: "gray.200",
  borderRadius: "md",
  fontFamily: "body",
  fontWeight: "normal",
  px: 2.5,
};

export function getActionAlertText(action: FormAction): string {
  switch (action) {
    case "start":
      return "Ready to start";
    case "resume":
      return "Already started, resume?";
    case "edit":
    case "late-edit":
      return "Already submitted, edit?";
    case "view":
      return "Already submitted and the form can not be edited. View it or ";
  }
}

export function getActionButtonText(action: FormAction): string {
  switch (action) {
    case "start":
      return "Start";
    case "resume":
      return "Resume";
    case "edit":
    case "late-edit":
      return "Edit";
    case "view":
      return "View";
  }
}

export const SelectFormTemplate: FunctionComponent<Props> = (props) => {
  const { eligibleTemplates, selectedStudent, isLoading, isError } = props;
  const organization = useOrganization();

  const [{ value: enrollmentPeriodId }] =
    useField<string>("enrollmentPeriodId");
  const [{ value: studentId }] = useField<string>("studentId");
  const [{ value: formTemplateId }, , formTemplateIdHelper] =
    useField<string>("formTemplateId");

  const formTemplateOptions: FormTemplateOption[] = useMemo(() => {
    if (!selectedStudent) {
      return eligibleTemplates
        .map((ft) => ({
          label: ft.name,
          description: ft.description,
          key: ft.id,
          value: ft.id,
          disabled: true,
          action: "start" as FormAction,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    // Start New Form only: match eligible-templates and existing rows to the selected enrollment period.
    const formsForSelectedEnrollmentPeriod = enrollmentPeriodId
      ? selectedStudent.forms.filter(
          (form) =>
            form.form_template.enrollment_period.id === enrollmentPeriodId
        )
      : [];

    const eligibleTemplateIds = new Set(eligibleTemplates.map((ft) => ft.id));

    const fromEligible: FormTemplateOption[] = eligibleTemplates.map((ft) => {
      const existingForm = selectedStudent.forms.find(
        (form) => form.form_template_id === ft.id
      );
      const action = getFormAction(existingForm);
      return {
        label: ft.name,
        description: ft.description,
        key: ft.id,
        value: ft.id,
        action,
        existingFormId: existingForm?.id,
      };
    });

    const fromExistingForms: FormTemplateOption[] =
      formsForSelectedEnrollmentPeriod
        .filter((form) => !eligibleTemplateIds.has(form.form_template_id))
        .map((form) => {
          const action = getFormAction(form);
          return {
            label: form.form_template.name,
            description: form.form_template.description,
            key: form.form_template_id,
            value: form.form_template_id,
            action,
            existingFormId: form.id,
          };
        });

    return [...fromEligible, ...fromExistingForms].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [eligibleTemplates, selectedStudent, enrollmentPeriodId]);

  const noFormTemplatesAreAvailable = formTemplateOptions.length === 0;

  const selectedOption = useMemo(
    () => formTemplateOptions.find((opt) => opt.key === formTemplateId) ?? null,
    [formTemplateOptions, formTemplateId]
  );

  // Intentionally omit formTemplateIdHelper from deps: including it retriggers when we
  // setValue and causes reset loops; we only want to react when the option list changes.
  useEffect(() => {
    const enableOptions = formTemplateOptions.filter(
      (option) => !option.disabled
    );
    if (enableOptions.length === 1 && enableOptions[0]) {
      formTemplateIdHelper.setValue(enableOptions[0].key);
    } else {
      formTemplateIdHelper.setValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTemplateOptions]);

  if (!enrollmentPeriodId || !studentId) {
    return null;
  }

  if (isLoading) {
    return (
      <Flex direction="column" gap={2}>
        <Text fontWeight="medium">What do you want to do?</Text>
        <Flex alignItems="center" gap={3} padding={3}>
          <Spinner size="sm" />
          <Text color="gray.600">Loading available forms...</Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <FormikCustomSelect
        renderEmptyItems={() => <Text>No available options</Text>}
        disabled={noFormTemplatesAreAvailable}
        name="formTemplateId"
        label={<>What do you want to do?</>}
        items={formTemplateOptions}
        renderItem={(item) => {
          const badge = getBadgeProps(item.action);
          return (
            <Flex direction="column" gap={1} width="100%">
              <Flex alignItems="center" gap={2} flexWrap="wrap" width="100%">
                <Text color="gray.900">{item.label}</Text>
                <Badge
                  fontSize="xs"
                  marginLeft="auto"
                  textTransform="none"
                  {...whiteBadgeProps}
                >
                  <Glossary>{badge.label}</Glossary>
                </Badge>
              </Flex>
              {item.description && (
                <Text color="blackAlpha.700">{item.description}</Text>
              )}
            </Flex>
          );
        }}
        renderSelectedItem={(item) => {
          if (!item) return "Select form";
          return item.label;
        }}
        openDirection={window.innerHeight < 800 ? "up" : "down"}
        maxHeight="250px"
      />
      {isError && (
        <InfoAlert status="error">
          <Text>Unable to get forms - please try again later.</Text>
        </InfoAlert>
      )}
      {noFormTemplatesAreAvailable && !isError && selectedStudent && (
        <NoDataInfoAlert
          text={`There are currently no forms available to start for ${fullName(
            selectedStudent
          )}.`}
          helpText="Click here for help."
        />
      )}
      {selectedOption && (
        <Alert
          status={selectedOption.action === "start" ? "success" : "info"}
          borderRadius={6}
          alignItems="center"
        >
          <AlertIcon />
          <AlertDescription flexGrow={1}>
            <Text
              color={
                selectedOption.action === "start" ? "green.700" : "blue.700"
              }
            >
              <Glossary>{getActionAlertText(selectedOption.action)}</Glossary>
              {selectedOption.action === "view" && (
                <>
                  {" "}
                  <Link
                    as={RouterLink}
                    to={organization
                      .map((org) => Url.Parent.help(org))
                      .withDefault("#")}
                    fontWeight="bold"
                    textDecoration="underline"
                  >
                    ask for help?
                  </Link>
                </>
              )}
            </Text>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
